import React, { useState } from "react";
import { Nav } from "react-bootstrap";
import { CommonButton, CommonModal } from "../Input";
import CreateSingleTransactionModal from "../Transactions/CreateSingleTransactionModal";
import SendBulkTransactionModal from "../Transactions/SendBulkTransactionModal";
import TopUpModal from "./TopUpModal";
import WithdrawalModal from "./WithdrawalModal";
import TransactionDetailsModal from "../Transactions/TransactionDetailsModal";

// Sub-components
import InfoTab from "./CustomerDetails/InfoTab";
import BeneficiaryTab from "./CustomerDetails/BeneficiaryTab";
import HistoryTab from "./CustomerDetails/HistoryTab";

import IndividualCustomerModal from "./IndividualCustomerModal";
import OrganizationCustomerModal from "./OrganizationCustomerModal";

const CustomerDetailsModal = ({ show, onHide, customer }) => {
  const [activeTab, setActiveTab] = useState("information");
  const [showSendTransactionModal, setShowSendTransactionModal] =
    useState(false);
  const [showSendBulkTransactionModal, setShowSendBulkTransactionModal] =
    useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showTransactionDetailsModal, setShowTransactionDetailsModal] =
    useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showEditIndividualModal, setShowEditIndividualModal] = useState(false);
  const [showEditOrganizationModal, setShowEditOrganizationModal] =
    useState(false);

  // History states
  // History states - Use data from customer or default mock
  const initialHistoryData = customer?.history || [
    {
      id: "TRX12345",
      dateTime: "09/03/2026\n15:30:00",
      senderName: customer?.name || "SparkMinds Technology",
      senderEmail: customer?.email || "uat-minds@spark.com",
      receiverName: "Nguyen Van A",
      paymentMethod: "Cash",
      sendAmount: "1,000.00 USD",
      status: "Approved",
    },
    {
      id: "TRX67890",
      dateTime: "08/03/2026\n10:15:22",
      senderName: customer?.name || "SparkMinds Technology",
      senderEmail: customer?.email || "uat-minds@spark.com",
      receiverName: "Le Thi B",
      paymentMethod: "Bank Transfer",
      sendAmount: "500.00 USD",
      status: "Pending",
    },
  ];

  const [historyData, setHistoryData] = useState(initialHistoryData);
  const [historyFilters, setHistoryFilters] = useState({
    id: "",
    method: "",
    status: "",
  });

  const handleHistorySearch = () => {
    let filtered = initialHistoryData.filter((item) => {
      const matchId = item.id
        .toLowerCase()
        .includes(historyFilters.id.toLowerCase());
      const matchMethod = historyFilters.method
        ? item.paymentMethod === historyFilters.method
        : true;
      const matchStatus = historyFilters.status
        ? item.status === historyFilters.status
        : true;
      return matchId && matchMethod && matchStatus;
    });
    setHistoryData(filtered);
  };

  const handleHistoryReset = () => {
    setHistoryFilters({ id: "", method: "", status: "" });
    setHistoryData(initialHistoryData);
  };

  if (!customer) return null;

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      title="Customer Details"
      size="xl"
      headerClassName="border-bottom pb-3"
      footer={
        <div className="w-100 text-end">
          <CommonButton variant="outline" onClick={onHide}>
            Close
          </CommonButton>
        </div>
      }
    >
      {/* Top Buttons Section */}
      {customer?.kycStatus === "APPROVED" && (
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap g-2">
          <div className="d-flex g-2">
            <CommonButton
              className="me-2"
              onClick={() => setShowSendTransactionModal(true)}
            >
              Send Single Transaction
            </CommonButton>
            <CommonButton onClick={() => setShowSendBulkTransactionModal(true)}>
              Send Bulk Transaction
            </CommonButton>
          </div>
          <div className="d-flex g-2">
            <CommonButton
              variant="outline"
              className="me-2"
              onClick={() => setShowWithdrawalModal(true)}
            >
              Withdrawal
            </CommonButton>
            <CommonButton onClick={() => setShowTopUpModal(true)}>
              Top-Up
            </CommonButton>
          </div>
        </div>
      )}

      {/* Updated Tabs using nav-tabs custom-tabs-1 */}
      <div className="custom-tab-1 pb-2">
        <Nav as="ul" className="nav nav-tabs custom-tabs-1 mb-3">
          <Nav.Item as="li">
            <Nav.Link
              eventKey="information"
              active={activeTab === "information"}
              onClick={() => setActiveTab("information")}
            >
              Information
            </Nav.Link>
          </Nav.Item>
          <Nav.Item as="li">
            <Nav.Link
              eventKey="beneficiary"
              active={activeTab === "beneficiary"}
              onClick={() => setActiveTab("beneficiary")}
            >
              Beneficiary
            </Nav.Link>
          </Nav.Item>
          <Nav.Item as="li">
            <Nav.Link
              eventKey="history"
              active={activeTab === "history"}
              onClick={() => setActiveTab("history")}
            >
              Transaction History
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </div>

      <div style={{ minHeight: "500px" }}>
        {activeTab === "information" && (
          <InfoTab
            customer={customer}
            onEdit={() => {
              if (customer.type === "Individual") {
                setShowEditIndividualModal(true);
              } else {
                setShowEditOrganizationModal(true);
              }
            }}
          />
        )}

        {activeTab === "beneficiary" && (
          <BeneficiaryTab beneficiaries={customer?.beneficiaries} />
        )}

        {activeTab === "history" && (
          <HistoryTab
            historyData={historyData}
            historyFilters={historyFilters}
            setHistoryFilters={setHistoryFilters}
            onSearch={handleHistorySearch}
            onReset={handleHistoryReset}
            onViewDetails={(item) => {
              setSelectedTransaction(item);
              setShowTransactionDetailsModal(true);
            }}
          />
        )}
      </div>
      <CreateSingleTransactionModal
        show={showSendTransactionModal}
        onHide={() => setShowSendTransactionModal(false)}
        customer={customer}
        hideStep0={true}
      />

      <SendBulkTransactionModal
        show={showSendBulkTransactionModal}
        onHide={() => setShowSendBulkTransactionModal(false)}
        customer={customer}
        hideStep0={true}
      />

      <TopUpModal
        show={showTopUpModal}
        onHide={() => setShowTopUpModal(false)}
        customer={customer}
        hideStep0={true}
      />

      <WithdrawalModal
        show={showWithdrawalModal}
        onHide={() => setShowWithdrawalModal(false)}
        customer={customer}
        hideStep0={true}
      />

      <TransactionDetailsModal
        show={showTransactionDetailsModal}
        onHide={() => setShowTransactionDetailsModal(false)}
        transaction={selectedTransaction}
      />

      <IndividualCustomerModal
        show={showEditIndividualModal}
        onHide={() => setShowEditIndividualModal(false)}
        customer={customer}
      />

      <OrganizationCustomerModal
        show={showEditOrganizationModal}
        onHide={() => setShowEditOrganizationModal(false)}
        customer={customer}
      />
    </CommonModal>
  );
};

export default CustomerDetailsModal;
